import { useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import InfoIcon from "@mui/icons-material/Info";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { usePostHog } from "posthog-js/react";
import { Stack, Button, Tooltip, Typography, IconButton } from "@financeable-com-au/financeable-ui";
import {
  SUCCESS_MESSAGE,
  ERRORS_MESSAGE,
  APPLICATION_DOWNLOAD_TYPE,
} from "@/constants";
import {
  applicationSelector,
  getCurrentProductLenderId,
  getApplicationDownloadUrl,
  userDetailsSelector,
  loanDetailsSelector,
  updateLoanDetails,
  saveValue,
  setSubmissionModal,
  getApplicationDocuments,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";
import {
  type ApplicationFormSlice,
  type ApplicationState,
  type UserDetailsState,
  type LoanDetails,
  type SubmissionModal,
} from "@/store/slices/types/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import { userSelector, userPermissionsSelector } from "@/store/slices/userSlice";
import type { UserSlice, UserPermissionsDocument, UserState } from "@/store/slices/types/userSlice";
import { quoteCalculatorSelector } from "@/store/slices/quoteCalculator";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { useSubmissionSteps } from "@/components/SubmissionModal/hooks/useSubmissionSteps";
import { DownloadApplicationButton } from "@/components/CommonComponents/DownloadApplicationButton";
import { isLodgeAISupported } from "@/utils/lenderUtils";
import { useFeatureFlags } from "@/utils/featureFlags/useFeatureFlags";
import { useLenderCardContext } from "@/providers/LenderCardContextProvider";
import { useAccessAppComponent } from "@/hooks/useAccessAppComponent";
import { buildLenderLoanDetailsPayload } from "../utils/buildLenderLoanDetailsPayload";

interface LenderDetailForSubmission extends Required<Pick<SubmissionModal, "lender" | "lodgeAI" | "productDetails">> {
  submitType: SubmissionModal["submissionType"];
}

interface LenderCardDownloadSubmitActionsProps {
  /** Horizontally center buttons (e.g. single-product condensed card). */
  centerActions?: boolean;
}

const LenderCardDownloadSubmitActions = ({ centerActions = false }: LenderCardDownloadSubmitActionsProps) => {
  const posthog = usePostHog();
  const dispatch = useDispatch();
  const { isLodgeAIEnabled } = useFeatureFlags();
  const { constructSubmissionSteps } = useSubmissionSteps();
  const { product, lenderCard, lenderCardCustom, requiredFields, isCustomQuote, inArrears } = useLenderCardContext();
  const { accessComponents } = useAccessAppComponent();
  const { defaultApplicationDownloadType } = accessComponents.application;
  const application = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);
  const loanDetails = useSelector<ApplicationFormSlice, LoanDetails>(loanDetailsSelector);
  const quoteCalculator = useSelector(quoteCalculatorSelector);
  const { currentProductLenderId, downloadApplicationLoader, isQuoteOpen } = useSelector<
    ApplicationFormSlice,
    UserDetailsState
  >(userDetailsSelector);
  const permissions = useSelector<UserSlice, UserPermissionsDocument>(userPermissionsSelector);
  const { lenderSettings } = useSelector<UserSlice, UserState>(userSelector);

  const [isSubmitting, setIsSubmitting] = useState<SubmissionModal["submissionType"] | null>();

  const isApplication = !isQuoteOpen;
  const isDisableEdit = !permissions?.application?.edit || !application?.canEditApplication;
  const isConfiguredForSubmit =
    product?.lenderCredentials && product?.lenderCredentials.length > 0
      ? lenderSettings?.some((setting) => setting.lender === product?.lender)
      : true;
  const isLodgeAI = isLodgeAISupported(product);
  const isLodgeAIUpload = isLodgeAIEnabled() && isLodgeAI;
  const isUploadApplicationEnabled =
    (isLodgeAIUpload && product?.actions?.includes("upload")) || (!isLodgeAI && product?.actions?.includes("upload"));
  const isDownloadingThisLender = downloadApplicationLoader && currentProductLenderId === product?.lender;
  const defaultDownloadType = defaultApplicationDownloadType || APPLICATION_DOWNLOAD_TYPE.PDF;

  const buildLoanDetailsPayload = () =>
    buildLenderLoanDetailsPayload({
      loanDetails,
      quoteCalculator,
      product,
      lenderCard,
      lenderCardCustom,
      isCustomQuote,
      inArrears,
      applicationType: application.applicationType,
    });

  const handle = {
    applicationSubmit: async ({ lender, submitType, productDetails, lodgeAI }: LenderDetailForSubmission) => {
      setIsSubmitting(submitType);
      await dispatch(updateLoanDetails(buildLoanDetailsPayload())).unwrap();
      dispatch(saveValue({ applyApplication: null }));
      dispatch(getCurrentProductLenderId(lender));
      dispatch(getApplicationDocuments({ applicationId: application._id }));
      const submissionSteps = constructSubmissionSteps({ lenderSubmissionStep: product?.submissionSteps ?? [] });
      const submissionModal: SubmissionModal = {
        steps: submissionSteps,
        isOpen: true,
        submissionType: submitType,
        lender,
        lodgeAI,
        productDetails,
        submission: {
          status: undefined,
          message: undefined,
        },
      };
      dispatch(setSubmissionModal(submissionModal));
      setIsSubmitting(null);
    },
    uploadApplication: ({ submitType }: { submitType: SubmissionModal["submissionType"] }) => {
      const lenderDetail: LenderDetailForSubmission = {
        submitType,
        lender: product?.lender ?? "",
        lodgeAI: product?.lodgeAI ?? { cloud: false, native: false },
        productDetails: {
          productName: product?.productName ?? "",
          productType: product?.productType ?? "",
        },
      };
      handle.applicationSubmit(lenderDetail);
    },
    downloadApplication: async ({
      applicationId,
      lender,
      downloadType,
    }: {
      applicationId: string;
      lender: string;
      downloadType: string;
    }) => {
      dispatch(getCurrentProductLenderId(lender));
      await dispatch(updateLoanDetails(buildLoanDetailsPayload())).unwrap();
      dispatch(
        getApplicationDownloadUrl({
          applicationId,
          downloadType,
        }),
        // @ts-expect-error: existing dispatch
      ).then(({ payload, error }) => {
        if (error) {
          dispatch(
            openSnackbar({
              message: payload.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
        } else {
          posthog?.capture("download_application_clicked", {
            applicationId,
            lender,
            organisation: application.user?.organisation,
            applicationType: application.applicationType,
          });
          dispatch(
            openSnackbar({
              message: SUCCESS_MESSAGE.generatePdf,
              intent: "success",
            }),
          );
        }
      });
    },
  };

  if (!isApplication || !product) {
    return null;
  }

  return (
    <Stack
      flexDirection="row"
      flexWrap="wrap"
      columnGap={3}
      rowGap={1}
      alignItems="center"
      justifyContent={centerActions ? "center" : "flex-start"}
      width={centerActions ? "100%" : undefined}
    >
      <DownloadApplicationButton
        defaultDownloadType={defaultDownloadType}
        isLoading={isDownloadingThisLender}
        disabled={isDisableEdit}
        onDownload={(downloadType) =>
          handle.downloadApplication({ applicationId: application._id, lender: product?.lender || "", downloadType })
        }
      />
      {(product?.actions?.includes("email") || product?.actions?.includes("upload")) && (
        <Stack flexDirection="row" alignItems="center">
          <Stack flexDirection="row" flexWrap="wrap" columnGap={isConfiguredForSubmit ? 3 : 0} rowGap={1}>
            {product?.actions?.includes("email") && (
              <div>
                <Button
                  variant="primary"
                  intent="neutral"
                  size="sm"
                  disabled={!requiredFields?.isFieldsCompleted || !isConfiguredForSubmit}
                  endDecorator={<UploadFileOutlinedIcon sx={{ fontSize: "1rem" }} />}
                  onClick={() => {
                    handle.uploadApplication({ submitType: "email" });
                  }}
                  loading={isSubmitting === "email"}
                >
                  Email Application
                </Button>
                {!isConfiguredForSubmit && (
                  <Tooltip
                    title={
                      <Stack padding={2} flexDirection="column" rowGap={2}>
                        <Typography level="body-sm" bold>
                          {`Looks like you're not setup with ${product?.lender}.`}
                        </Typography>
                        <Typography mb={2}>Click on the link below to add your credentials.</Typography>
                        <Link to="/settings/lenders">
                          <Button variant="secondary" size="sm">
                            Add Credentials
                          </Button>
                        </Link>
                      </Stack>
                    }
                  >
                    <IconButton aria-label="Tooltip" isRounded={true} size="sm" variant="inline">
                      <InfoIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            )}
            {isUploadApplicationEnabled && (
              <div>
                <Button
                  variant="primary"
                  intent="neutral"
                  size="sm"
                  disabled={!requiredFields?.isFieldsCompleted || !isConfiguredForSubmit}
                  endDecorator={<UploadFileOutlinedIcon sx={{ fontSize: "1rem" }} />}
                  onClick={() => {
                    handle.uploadApplication({ submitType: isLodgeAIUpload ? "browser" : "upload" });
                  }}
                  loading={isSubmitting === "upload"}
                >
                  Upload Application
                </Button>
                {!isConfiguredForSubmit && (
                  <Tooltip
                    title={
                      <Stack padding={2} flexDirection="column" rowGap={2}>
                        <Typography level="body-sm" bold>
                          {`Looks like you're not setup with ${product?.lender}.`}
                        </Typography>
                        <Typography mb={2}>Click on the link below to add your credentials.</Typography>
                        <Link to="/settings/lenders">
                          <Button variant="secondary" size="sm">
                            Add Credentials
                          </Button>
                        </Link>
                      </Stack>
                    }
                  >
                    <IconButton aria-label="Tooltip" isRounded={true} size="sm" variant="inline">
                      <InfoIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            )}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export { LenderCardDownloadSubmitActions };
